import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material"
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import AlertMessages from "common/Alert/AlertMessages";
import { useNavigate, useParams } from "react-router-dom";
import { InventoryProfileService } from "subModule/src/core/services";
import { StockAdjustment } from "subModule/src/services/inventory/interface";
import Loader from "layout/Loader";
import { useTranslation } from "react-i18next";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import InventoryIcon from "@mui/icons-material/Inventory";
import { useFrozenInventoryDocBreadcrumbs } from "Helpers/useFrozenInventoryBreadcrumbs";

const PrintStockAdjustment: React.FC = () => {
    const params = useParams();
    const { t, i18n } = useTranslation();
    const branding = useSelector(brandingInfo);
    const navigate = useNavigate();
    const { breadcrumbItems, captureLabel } = useFrozenInventoryDocBreadcrumbs(
      "stock-adjustment",
      { print: true },
    );
    const themeColors = branding?.attributes?.themeId === 100 ? branding?.attributes?.customTheme : (themes as any)[branding?.attributes?.themeId || 3];
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState<boolean>(true);
    const [StockAdjustment, setStockAdjustment] = useState<StockAdjustment>();
    const columns = [
        { id: 'itemId' },
        { id: 'itemCode'},
        { id: 'unitId' },
        { id: 'itemName', label: t('Item'), numeric: false, disablePadding: false, type: 'text' },
        ...(params?.id ? [] : [{ id: 'baseItemQuantity', label: t('availableQuantity') }]),
        { id: 'quantityAdjusted', label: t('Quantity'), numeric: false, disablePadding: false, type: 'number' },
        { id: 'unitTitle', label: t('unit') },
        { id: 'adjustmentType', label: t('adjustmentType'), numeric: false, disablePadding: false, type: 'number' },
        { id: 'unitCostAdjusted', label: t('Cost'), numeric: false, disablePadding: false, type: 'number' },
        { id: 'adjustmentReason', label: t('adjustmentReason'), numeric: false, disablePadding: false, type: 'text' },
    ];
    useEffect(() => {
        if(loading) getStockAdjustmentById();
    }, []);
    const getStockAdjustmentById = () => {
        InventoryProfileService.getStockAdjustmentById(params?.id).then((res: any) => {
            if(res.success) {
                setStockAdjustment((res.data as any));
                captureLabel(
                  (res.data as any)?.itemCode ||
                    (res.data as any)?.storeTitle ||
                    (params?.id ? String(params.id) : null),
                );
                setLoading(false);
            } else {
                setLoading(false);
                enqueueSnackbar(<AlertMessages text={res.message || "Something went wrong"} />, { variant: 'error' });
            }
        }).catch((err:any) => { console.error("===", err); });
    }
    return (
        <Box>
            <ComponentHeaderTwo
                breadcrumbItems={breadcrumbItems}
                showBtn={true}
                showSaveBtn={false}
                onClick={() => navigate('/inventory/stock-adjustments')}
                heading={t('StockAdjustments')}
                icon={
                  <InventoryIcon
                    sx={{ fill: "#000", width: "28px", height: "28px" }}
                  />
                }
                btnText={"back"}
                btnType={"back"}
                showCreateBtn={false}
            />
            {loading ? <Loader /> : <>
            <Box className='max-w-[1100px] bg-white py-[10px] px-[20px] rounded-[8px] block mx-auto hide-on-print mb-4'>
                <Box className='flex justify-between items-center' id='form'>
                    <Typography className="font-Saira text-[12px] font-semibold" style={{ color: themeColors?.primary }}>{t('StockAdjustment')}</Typography>
                    <Box className='flex gap-2'>
                        <Button className="secondary" variant="contained" style={{ color: themeColors?.primary }} disableElevation onClick={() => navigate('/inventory/stock-adjustments')}>{t('back')}</Button>
                        <Button className="primary" variant="contained" style={{ background: themeColors?.primary }} disableElevation onClick={() => window.print()}>{t('Print')}</Button>
                    </Box>
                </Box>
            </Box>
            <Box id="print-root" className='max-w-[1100px] bg-white p-[20px] rounded-[8px] block mx-auto'>
                <Box className='flex justify-end mb-2'>
                    <Box>
                        <Typography className="font-Saira text-[12px] text-end font-semibold">
                            <span>{StockAdjustment?.createdAt ? Intl.DateTimeFormat(i18n.language, { year: 'numeric', month: 'long', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit', second: '2-digit', 
                            numberingSystem: i18n.language === 'ur' || i18n.language === 'ar' ? 'arab' : 'latn' 
                        }).format(new Date(StockAdjustment.createdAt)) : null}</span>
                        </Typography>
                    </Box>
                </Box>
                <Box className='flex justify-between items-center py-3 px-2 rounded-[8px] mb-2' style={{ background: themeColors?.appBg }}>
                    <Typography className="font-Saira text-[12px] text-left">
                        <span className="italic font-medium">{t('store')}: </span> 
                        <span className="font-bold">{StockAdjustment?.storeTitle || 0}</span>
                    </Typography>
                    <Box className="flex gap-3 items-center">
                        <Typography className="font-Saira text-[12px] text-left">
                            <span className="italic font-medium">{t('createdBy')}: </span>
                            <span className="font-bold">{StockAdjustment?.createdBy || ''}</span>
                        </Typography>
                    </Box>
                </Box>
                <Typography className="font-Saira text-[12px] font-semibold text-[#333843] text-left mb-2">{t('StockAdjustment')}</Typography>
                <Box className='mb-4 w-[100%]' id="inventorytable">
                    <TableContainer>
                        <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size={'small'}>
                            <TableHead>
                                <TableRow sx={{ borderBottom: `1px solid #B8BDCB` }}>
                                    {columns.filter((column) => column.id !== 'itemId' && column.id !== 'itemCode' && column.id !== 'unitId').map((column) => (
                                        <TableCell
                                            key={column.id}
                                            align={column.numeric ? 'right' : 'left'}
                                            padding={column.disablePadding ? 'none' : 'normal'}
                                            sortDirection={false}
                                            className="font-bold text-center"
                                        >{column.label}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody sx={{ '& tr:nth-child(odd)': { background: `transparent !important` }, '& tr': {  borderBottom: `1px solid #B8BDCB` }, '& tr:last-child': { borderBottom: 0 } }}>
                                {([{...StockAdjustment}]).map((row, index) => {
                                    const labelId = `enhanced-table-checkbox-${index}`;
                                    return (
                                        <TableRow hover tabIndex={-1} key={row.id} sx={{ height: '36px' }}>
                                            {columns.filter((column) => column.id !== 'itemId' && column.id !== 'itemCode' && column.id !== 'unitId').map((column) => (
                                                <TableCell
                                                    key={column.id}
                                                    align={column.numeric ? 'right' : 'left'}
                                                    padding={column.disablePadding ? 'none' : 'normal'}
                                                    component={column.id === 'itemName' ? 'th' : undefined}
                                                    id={column.id === 'itemName' ? labelId : undefined}
                                                    scope={column.id === 'itemName' ? 'row' : undefined}
                                                    className="text-center"
                                                >{column.id === 'itemName' ? ((row as any)[column.id] || '') 
                                                    : (column.id === "adjustmentType") ? [
                                                        { value: 1, label: t('remove') },
                                                        { value: 2, label: t('add') },
                                                      ].find((option: any) => option.value === row['adjustmentType'])?.label
                                                    : column.id === 'unitTitle' ? ((row as any)[column.id] || t('unit(s)'))
                                                    : (row as any)[column.id]
                                                }</TableCell>
                                            ))}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box>
            </>}
        </Box>
    )
}

export default PrintStockAdjustment;